import { RelayClientMessageSchema, RelayServerMessageSchema, SignedOperationSchema, SyncEnvelopeSchema, } from "./schemas.js";
export const MAX_ENVELOPE_BYTES = 512 * 1024;
export const DEFAULT_RETENTION_MS = 30 * 24 * 60 * 60 * 1000;
export function encodeClientMessage(message) {
    return JSON.stringify(RelayClientMessageSchema.parse(message));
}
export function decodeClientMessage(raw) {
    if (raw.length > MAX_ENVELOPE_BYTES * 2)
        throw new Error("Relay message too large");
    return RelayClientMessageSchema.parse(JSON.parse(raw));
}
export function encodeServerMessage(message) {
    return JSON.stringify(RelayServerMessageSchema.parse(message));
}
export function decodeServerMessage(raw) {
    if (raw.length > MAX_ENVELOPE_BYTES * 2)
        throw new Error("Relay message too large");
    return RelayServerMessageSchema.parse(JSON.parse(raw));
}
/**
 * `encodedLength` e' la lunghezza del messaggio da cui l'envelope arriva, gia'
 * nota a chi l'ha letto dal socket. E' un limite superiore della forma
 * serializzata dell'envelope — l'involucro `publish` aggiunge qualche decina di
 * byte e lo schema scarta i campi che non conosce — quindi misurare li' e'
 * conservativo: si rifiuta al piu' qualche byte prima del tetto, mai dopo.
 * Senza, la misura costava una serializzazione completa buttata via a ogni
 * pubblicazione (PERF-11).
 */
export function validateEnvelopeForRelay(envelope, now = Date.now(), encodedLength) {
    const parsed = SyncEnvelopeSchema.parse(envelope);
    if (Date.parse(parsed.expiresAt) <= now)
        throw new Error("Envelope expired");
    const length = encodedLength ?? JSON.stringify(parsed).length;
    if (length > MAX_ENVELOPE_BYTES)
        throw new Error("Envelope too large");
    return parsed;
}
export function validateDecryptedOperation(value, expectedGymId) {
    const operation = SignedOperationSchema.parse(value);
    if (operation.gymId !== expectedGymId)
        throw new Error("Cross-gym operation rejected");
    return operation;
}
/**
 * La coda locale si ferma prima del relay: il client conserva al massimo 500
 * item, mentre `RELAY_MAX_RECORDS_PER_SENDER` ne accetta 700. I 200 posti di
 * margine assorbono envelope gia' pubblicati ma non ancora confermati senza
 * permettere a un singolo dispositivo di accodare indefinitamente. L'errore
 * resta visibile quando il guasto nasce, non una settimana dopo.
 */
export const MAX_QUEUE_ITEMS = 500;
/** La coda e' piena: chi accoda deve saperlo, non scoprirlo dal relay. */
export class SyncQueueFullError extends Error {
    constructor() {
        super("Coda di sincronizzazione piena: nulla e' partito da troppo tempo");
        this.name = "SyncQueueFullError";
    }
}
/**
 * Il tetto e' un parametro perche' l'attesa accettabile dipende da chi guarda:
 * l'app in primo piano ne vuole uno ridotto, quella nascosta no. La decisione
 * resta al chiamante, cosi' questa funzione non impara nulla del DOM.
 */
export function nextBackoff(attempts, now = Date.now(), capMs = 60_000) {
    const delay = Math.min(capMs, 500 * 2 ** Math.min(attempts, 7));
    return new Date(now + delay).toISOString();
}
/**
 * La minimizzazione e' per ruolo, non per identita': a un membro va la sola
 * pianificazione propria, a un coach o a un owner tutte — che e' esattamente
 * l'insieme che il suo ruolo puo' gia' leggere. Consegnargliene meno lo
 * lascerebbe con una palestra cieca dopo un ripristino.
 */
export function selectRecoveryState(gym, schedules, requesterId, requesterRole = "member") {
    if (requesterRole === "member") {
        const own = schedules.find((candidate) => candidate.memberId === requesterId);
        return { gym, schedules: own ? [own] : [] };
    }
    return { gym, schedules: [...schedules] };
}
